"use client";

import Link from "next/link";
import { motion } from "@/lib/motion";
import { EyebrowBadge } from "@/components/ui/eyebrow-badge";
import { HeroGlow } from "@/components/ui";
import { mergeWithPrismic, CATEGORY_STYLES } from "./data";
import type { PrismicLegalEntry } from "@/app/(marketing)/legal/page";

type Props = {
  prismicDocs?: PrismicLegalEntry[];
};

export default function LegalArchiveSection({ prismicDocs = [] }: Props) {
  const allDocs = mergeWithPrismic(prismicDocs);

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-h-screen"
      style={{ background: "var(--background)" }}
    >
      {/* Hero */}
      <section
        className="relative overflow-y-clip border-b pt-24 sm:pt-32"
        style={{ borderColor: "var(--border)" }}
      >
        <HeroGlow align="left" />
        <div className="relative mx-auto max-w-7xl px-4 pb-10 sm:px-6 sm:pb-20 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <EyebrowBadge>Legal</EyebrowBadge>
            <h1 className="text-foreground mb-4 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Legal
            </h1>
            <p className="text-muted-foreground light:text-foreground/80 max-w-2xl text-lg leading-relaxed">
              Our legal documents cover your rights, our responsibilities, and the rules that govern
              use of Proxidize products and services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Documents grid */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-20 lg:px-8">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
          {allDocs.map((doc, i) => {
            const style = CATEGORY_STYLES[doc.category];
            return (
              <motion.div
                key={doc.slug}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
              >
                <Link
                  href={"href" in doc && doc.href ? doc.href : `/legal/${doc.slug}`}
                  className="group flex h-full flex-col rounded-xl p-6 transition-all duration-200"
                  style={{ background: "var(--surface-1)", border: "1px solid var(--border)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      "color-mix(in srgb, var(--foreground) 15%, transparent)";
                    (e.currentTarget as HTMLElement).style.background = "var(--surface-2)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--border)";
                    (e.currentTarget as HTMLElement).style.background = "var(--surface-1)";
                  }}
                >
                  <span
                    className="mb-4 inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1 text-xs font-medium"
                    style={{
                      background:
                        style?.badge ?? "color-mix(in srgb, var(--foreground) 6%, transparent)",
                      color: style?.dot ?? "var(--foreground)",
                    }}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: style?.dot ?? "var(--foreground)" }}
                    />
                    {doc.category}
                  </span>
                  <h2
                    className="mb-2 text-base leading-snug font-semibold"
                    style={{ color: "var(--foreground)" }}
                  >
                    {doc.title}
                  </h2>
                  <p
                    className="flex-1 text-sm leading-relaxed"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {doc.description}
                  </p>
                  <div
                    className="mt-5 flex items-center justify-between pt-4"
                    style={{ borderTop: "1px solid var(--border)" }}
                  >
                    <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                      Updated {doc.lastUpdated}
                    </span>
                    <span
                      className="flex items-center gap-1 text-xs transition-colors"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      Read
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 8h10M9 4l4 4-4 4"
                        />
                      </svg>
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="relative mt-14 flex flex-col items-start gap-4 overflow-hidden rounded-xl px-6 py-5 sm:flex-row sm:items-center"
          style={{ background: "var(--surface-1)", border: "1px solid rgba(91,140,255,0.25)" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at top left, rgba(91,140,255,0.18) 0%, rgba(91,140,255,0.06) 35%, transparent 70%)",
            }}
          />
          <div
            className="relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
            style={{ background: "rgba(91,140,255,0.15)" }}
          >
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              style={{ color: "var(--brand-green)" }}
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p
            className="relative flex-1 text-sm leading-relaxed"
            style={{ color: "var(--muted-foreground)" }}
          >
            Questions about our legal documents?
          </p>
          <Link
            href="/contact"
            className="bg-foreground text-background relative inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          >
            Contact our legal team
          </Link>
        </motion.div>
      </section>
    </main>
  );
}
